import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  IconButton,
  Link,
  Switch,
  FormControlLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import {
  styled
} from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import MemoryIcon from '@mui/icons-material/Memory';
import DevicesIcon from '@mui/icons-material/Devices';
import HardwareIcon from '@mui/icons-material/Hardware';
import SecurityIcon from '@mui/icons-material/Security';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import Replicate from 'replicate';
import supabaseClient from '../../supabaseClient';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '../../context/AuthContext';
import { createClient } from '@supabase/supabase-js';

// API keys and configuration
const CLARIFAI_API_KEY = '779cf5ef113d42e6841001eb47de5f5e';
const CLARIFAI_API_URL = 'https://api.clarifai.com/v2/models/';
const CLARIFAI_MODEL_ID = 'general-image-recognition'; // Using general model, can be changed to more specific ones

// OpenAI configuration for detailed analysis
const OPENAI_MODEL = 'gpt-4-vision-preview';

// Replicate configuration
const REPLICATE_API_TOKEN = 'r8_DFa3DActQAgJTXoUoVDTmQMMREWQzNc2vGaxF';
const REPLICATE_MODEL = 'yorickvp/llava-13b:2facb4a474a0462c15041b78b1ad70952ea46b5ec6ad29583c0b29dbd4249591';

// Claude API configuration
const CLAUDE_API_TOKEN = 'r8_DFa3DActQAgJTXoUoVDTmQMMREWQzNc2vGaxF'; // Using the same token as provided
const CLAUDE_MODEL = 'claude-3-opus-20240229';

// DeepAI configuration
const DEEPAI_API_KEY = '6cc8b50f-979b-43e7-82b9-aba558a43202';
const DEEPAI_API_URL = 'https://api.deepai.org/api/image-captioning';

// Hardware issue keywords for caption analysis
const HARDWARE_ISSUE_KEYWORDS = [
  'broken', 'damaged', 'cracked', 'faulty', 'issue', 'problem', 'error',
  'malfunction', 'defective', 'worn', 'burnt', 'corroded', 'leaking',
  'disconnected', 'loose', 'missing', 'overheating', 'short', 'circuit',
  'failure', 'failed', 'not working', 'malfunctioning', 'dead', 'fried'
];

// Initialize Replicate client
const replicate = new Replicate({
  auth: REPLICATE_API_TOKEN,
});

// Model types enum
const MODEL_TYPES = {
  OPENAI: 'openai',
  REPLICATE: 'replicate',
  CLAUDE: 'claude',
  DEEPAI: 'deepai'
};

// Styled components with blue theme
const UploadBox = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  backgroundColor: '#0a1929',
  border: '2px dashed #3f8cb8',
  borderRadius: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  minHeight: 200,
  '&:hover': {
    borderColor: '#64b5f6',
    backgroundColor: '#132f4c',
  },
}));

const ImagePreview = styled('img')({
  maxWidth: '100%',
  maxHeight: '300px',
  objectFit: 'contain',
  borderRadius: '8px',
  marginTop: '16px',
});

const ResultCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#0a1929',
  color: '#fff',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  overflow: 'hidden',
  height: '100%',
  border: '1px solid rgba(100, 181, 246, 0.3)',
}));

const ResultHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#132f4c',
  padding: theme.spacing(2),
  borderBottom: '1px solid rgba(100, 181, 246, 0.3)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ResultContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const TagChip = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  backgroundColor: 'rgba(100, 181, 246, 0.2)',
  color: '#64b5f6',
  borderRadius: '16px',
  padding: '4px 12px',
  margin: '4px',
  fontSize: '0.85rem',
  fontWeight: 500,
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const PolicyLink = styled(Link)(({ theme }) => ({
  color: '#64b5f6',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const IssueWarning = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: 'rgba(211, 47, 47, 0.1)',
  color: '#f44336',
  border: '1px solid rgba(211, 47, 47, 0.3)',
  '& .MuiAlert-icon': {
    color: '#f44336'
  }
}));

/**
 * ClarifaiAnalyzer Component
 * 
 * This component allows users to upload hardware images for AI analysis.
 * It uses Clarifai for initial image recognition, OpenAI for detailed hardware analysis,
 * and DeepAI for image captioning and issue detection.
 * Images are stored in Supabase storage and analysis results in the database.
 */
const ClarifaiAnalyzer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  // State management
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [detailedAnalysis, setDetailedAnalysis] = useState('');
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [modelType, setModelType] = useState(MODEL_TYPES.OPENAI);
  const [imageCaption, setImageCaption] = useState('');
  const [potentialIssues, setPotentialIssues] = useState([]);
  const [hasIssues, setHasIssues] = useState(false);

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('File size exceeds 5MB limit', 'error');
        return;
      }

      if (!file.type.startsWith('image/')) {
        showSnackbar('Please select an image file', 'error');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setAnalysisResults(null);
      setDetailedAnalysis('');
    }
  };

  // Handle drag and drop
  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('File size exceeds 5MB limit', 'error');
        return;
      }

      if (!file.type.startsWith('image/')) {
        showSnackbar('Please select an image file', 'error');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setAnalysisResults(null);
      setDetailedAnalysis('');
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  // Reset all states
  const handleReset = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResults(null);
    setDetailedAnalysis('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Show snackbar notifications
  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Upload image to Supabase storage
  const uploadImageToSupabase = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data, error } = await supabaseClient
        .storage
        .from('hardwareimages')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error(`Error uploading to Supabase: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabaseClient
        .storage
        .from('hardwareimages')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  // Save analysis to Supabase database
  const saveAnalysisToDatabase = async (imageUrl, clarifaiResults, openaiAnalysis) => {
    try {
      // Create a simple data object regardless of authentication state
      const analysisData = {
        image_url: imageUrl,
        clarifai_results: clarifaiResults,
        openai_analysis: openaiAnalysis,
        created_at: new Date().toISOString(),
        is_public: true // Set all records as public for now
      };
      
      // Only add user_id if user exists
      if (user?.id) {
        analysisData.user_id = user.id;
      }
      
      console.log('Attempting to save analysis with data:', { ...analysisData, clarifai_results: '[truncated]' });
      
      // First attempt: Try standard insert
      const { data, error } = await supabaseClient
        .from('hardware_analyses')
        .insert([analysisData]);

      if (!error) {
        console.log('Successfully saved analysis to database');
        return data;
      }
      
      console.error('First attempt failed:', error);
      
      // If we get here, the first attempt failed
      
      // Second attempt: Try with upsert
      console.log('Trying upsert approach...');
      const { data: upsertData, error: upsertError } = await supabaseClient
        .from('hardware_analyses')
        .upsert([analysisData]);
        
      if (!upsertError) {
        console.log('Successfully saved analysis using upsert');
        return upsertData;
      }
      
      console.error('Upsert attempt failed:', upsertError);
      
      // Third attempt: Try with a fresh client without RLS
      console.log('Attempting final approach with fresh client...');
      
      // Create a fresh client without any session
      const SUPABASE_URL = 'https://pzbtscdnhhmznlomdqwz.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB6YnRzY2RuaGhtem5sb21kcXd6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzg2OTExODEsImV4cCI6MjA1NDI2NzE4MX0.wj2VORXxT3vKI6oJj08kfTKfKKZjz9xKsyx6lArN5pI';
      const freshClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
      
      // Try to insert with the fresh client
      const { data: freshData, error: freshError } = await freshClient
        .from('hardware_analyses')
        .insert([{
          ...analysisData,
          // Ensure these fields are present and valid
          is_public: true,
          user_id: null // Explicitly set to null for anonymous submissions
        }]);
        
      if (freshError) {
        console.error('All attempts failed. Final error:', freshError);
        throw new Error(`Unable to save analysis: ${freshError.message}`);
      }
      
      console.log('Successfully saved analysis with fresh client approach');
      return freshData;
    } catch (error) {
      console.error('Database save error:', error);
      throw error;
    }
  };

  // Analyze image with Clarifai
  const analyzeClarifai = async (imageUrl) => {
    try {
      // For base64 data
      let imageData;
      if (imageUrl.startsWith('data:')) {
        imageData = {
          base64: imageUrl.split(',')[1]
        };
      } else {
        // For URL
        imageData = {
          url: imageUrl
        };
      }

      const response = await axios({
        method: 'POST',
        url: `${CLARIFAI_API_URL}${CLARIFAI_MODEL_ID}/outputs`,
        headers: {
          'Authorization': `Key ${CLARIFAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        data: {
          inputs: [
            {
              data: {
                image: imageData
              }
            }
          ]
        }
      });

      return response.data.outputs[0].data.concepts;
    } catch (error) {
      console.error('Clarifai analysis error:', error);
      throw error;
    }
  };

  // Get detailed analysis from OpenAI
  const getOpenAIAnalysis = async (imageUrl, clarifaiConcepts) => {
    try {
      // This would typically be a server-side call to protect your API key
      // For demo purposes, we're making a direct call
      // In production, route this through your backend
      
      const hardwareRelatedConcepts = clarifaiConcepts
        .filter(concept => concept.value > 0.7)
        .map(concept => concept.name)
        .join(', ');

      const response = await axios({
        method: 'POST',
        url: 'https://api.openai.com/v1/chat/completions',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        data: {
          model: OPENAI_MODEL,
          messages: [
            {
              role: "system",
              content: "You are a hardware expert specialized in identifying and analyzing computer and electronic components from images. Provide detailed, technical information about the hardware shown."
            },
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: `Analyze this hardware image in detail. Clarifai identified these concepts: ${hardwareRelatedConcepts}. Provide: 1) What specific hardware is shown, 2) Its typical specifications and capabilities, 3) Common uses, 4) Any compatibility information, 5) Estimated age/generation of the technology.`
                },
                {
                  type: "image_url",
                  image_url: {
                    url: imageUrl.startsWith('data:') ? imageUrl : imageUrl
                  }
                }
              ]
            }
          ],
          max_tokens: 500
        }
      });

      return response.data.choices[0].message.content;
    } catch (error) {
      console.error('OpenAI analysis error:', error);
      throw error;
    }
  };

  // Get detailed analysis from Replicate
  const getReplicateAnalysis = async (imageUrl, clarifaiConcepts) => {
    try {
      const hardwareRelatedConcepts = clarifaiConcepts
        .filter(concept => concept.value > 0.7)
        .map(concept => concept.name)
        .join(', ');

      const response = await replicate.predictions.create({
        version: REPLICATE_MODEL,
        input: {
          prompt: `Analyze this hardware image in detail. Clarifai identified these concepts: ${hardwareRelatedConcepts}. Provide: 1) What specific hardware is shown, 2) Its typical specifications and capabilities, 3) Common uses, 4) Any compatibility information, 5) Estimated age/generation of the technology.`,
          image: imageUrl.startsWith('data:') ? imageUrl : imageUrl
        }
      });

      return response.output;
    } catch (error) {
      console.error('Replicate analysis error:', error);
      throw error;
    }
  };

  // Get detailed analysis from Claude
  const getClaudeAnalysis = async (imageUrl, clarifaiConcepts) => {
    try {
      const hardwareRelatedConcepts = clarifaiConcepts
        .filter(concept => concept.value > 0.7)
        .map(concept => concept.name)
        .join(', ');

      const response = await axios({
        method: 'POST',
        url: 'https://api.claude.ai/v1/generate',
        headers: {
          'Authorization': `Bearer ${CLAUDE_API_TOKEN}`,
          'Content-Type': 'application/json'
        },
        data: {
          model: CLAUDE_MODEL,
          prompt: `Analyze this hardware image in detail. Clarifai identified these concepts: ${hardwareRelatedConcepts}. Provide: 1) What specific hardware is shown, 2) Its typical specifications and capabilities, 3) Common uses, 4) Any compatibility information, 5) Estimated age/generation of the technology.`,
          image: imageUrl.startsWith('data:') ? imageUrl : imageUrl
        }
      });

      return response.data.output;
    } catch (error) {
      console.error('Claude analysis error:', error);
      throw error;
    }
  };

  // Get image caption from DeepAI
  const getDeepAICaption = async (imageUrl) => {
    try {
      // For base64 data, we need to convert it to a Blob/File
      let formData = new FormData();
      
      if (imageUrl.startsWith('data:')) {
        // Convert base64 to blob
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        formData.append('image', blob);
      } else {
        // For URL
        formData.append('image', imageUrl);
      }

      const response = await fetch(DEEPAI_API_URL, {
        method: 'POST',
        headers: {
          'Api-Key': DEEPAI_API_KEY
        },
        body: formData
      });

      const data = await response.json();
      
      if (data.err) {
        throw new Error(`DeepAI error: ${data.err}`);
      }
      
      return data.output;
    } catch (error) {
      console.error('DeepAI caption error:', error);
      throw error;
    }
  };
  
  // Analyze caption for potential hardware issues
  const analyzeCaption = (caption) => {
    if (!caption) return { hasIssues: false, issues: [] };
    
    const captionLower = caption.toLowerCase();
    const foundIssues = HARDWARE_ISSUE_KEYWORDS.filter(keyword => 
      captionLower.includes(keyword.toLowerCase())
    );
    
    return {
      hasIssues: foundIssues.length > 0,
      issues: foundIssues
    };
  };
  
  // Get detailed analysis from DeepAI
  const getDeepAIAnalysis = async (imageUrl) => {
    try {
      // 1. Get image caption
      const caption = await getDeepAICaption(imageUrl);
      setImageCaption(caption);
      
      // 2. Analyze caption for potential issues
      const { hasIssues, issues } = analyzeCaption(caption);
      setPotentialIssues(issues);
      setHasIssues(hasIssues);
      
      // 3. Generate a more detailed analysis based on the caption
      let analysis = `DeepAI Caption: "${caption}"\n\n`;
      
      if (hasIssues) {
        analysis += `⚠️ Potential hardware issues detected: ${issues.join(', ')}\n\n`;
        analysis += `This image may show hardware with the following problems:\n`;
        issues.forEach(issue => {
          analysis += `- ${issue.charAt(0).toUpperCase() + issue.slice(1)} components or connections\n`;
        });
        analysis += `\nRecommendation: Consider further inspection or professional assessment of this hardware.`;
      } else {
        analysis += `No obvious hardware issues detected from the caption.\n`;
        analysis += `The hardware appears to be in normal condition based on initial assessment.`;
      }
      
      return analysis;
    } catch (error) {
      console.error('DeepAI analysis error:', error);
      throw error;
    }
  };

  // Main analysis function
  const analyzeImage = async () => {
    if (!selectedImage) {
      showSnackbar('Please select an image first', 'warning');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setImageCaption('');
    setPotentialIssues([]);
    setHasIssues(false);

    try {
      // 1. Upload image to Supabase
      const imageUrl = await uploadImageToSupabase(selectedImage);
      showSnackbar('Image uploaded successfully', 'success');

      // 2. Get Clarifai analysis
      const clarifaiResults = await analyzeClarifai(imagePreview);
      setAnalysisResults(clarifaiResults);
      showSnackbar('Initial analysis complete', 'success');

      // 3. Get detailed analysis based on selected model
      let detailedAnalysis;
      switch (modelType) {
        case MODEL_TYPES.OPENAI:
          detailedAnalysis = await getOpenAIAnalysis(imageUrl, clarifaiResults);
          break;
        case MODEL_TYPES.REPLICATE:
          detailedAnalysis = await getReplicateAnalysis(imageUrl, clarifaiResults);
          break;
        case MODEL_TYPES.CLAUDE:
          detailedAnalysis = await getClaudeAnalysis(imageUrl, clarifaiResults);
          break;
        case MODEL_TYPES.DEEPAI:
          detailedAnalysis = await getDeepAIAnalysis(imageUrl);
          break;
        default:
          throw new Error('Invalid model type');
      }
      setDetailedAnalysis(detailedAnalysis);

      // 4. Save results to database
      await saveAnalysisToDatabase(imageUrl, clarifaiResults, detailedAnalysis);
      showSnackbar('Analysis saved to database', 'success');

    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'An error occurred during analysis');
      showSnackbar('Analysis failed: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleModelChange = (event, newModelType) => {
    if (newModelType !== null) {
      setModelType(newModelType);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, backgroundColor: '#071426', borderRadius: '12px', mt: 2, mb: 2 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#64b5f6', fontWeight: 700 }}>
          Hardware Image Analysis
        </Typography>
        <Typography variant="body1" sx={{ color: '#e0e0e0', maxWidth: '800px', mx: 'auto' }}>
          Upload an image of computer hardware or electronic components for AI-powered identification and detailed analysis.
          Our system combines Clarifai's visual recognition with OpenAI's technical expertise.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <UploadBox
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
            >
              {!imagePreview ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 60, color: '#64b5f6', mb: 2 }} />
                  <Typography variant="h6" align="center" sx={{ color: '#fff' }}>
                    Drag & drop an image here or click to browse
                  </Typography>
                  <Typography variant="body2" align="center" sx={{ color: '#aaa', mt: 1 }}>
                    Supported formats: JPG, PNG, WEBP (Max: 5MB)
                  </Typography>
                </>
              ) : (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <ImagePreview src={imagePreview} alt="Selected hardware" />
                </Box>
              )}
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </UploadBox>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleReset}
              disabled={!selectedImage || isAnalyzing}
              sx={{
                backgroundColor: '#333',
                color: '#fff',
                '&:hover': { backgroundColor: '#555' }
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              onClick={analyzeImage}
              disabled={!selectedImage || isAnalyzing}
              sx={{
                backgroundColor: '#2196f3',
                color: '#fff',
                '&:hover': { backgroundColor: '#1976d2' }
              }}
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze Hardware'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
            <ToggleButtonGroup
              value={modelType}
              exclusive
              onChange={handleModelChange}
              aria-label="Model selection"
            >
              <ToggleButton value={MODEL_TYPES.OPENAI} aria-label="OpenAI">
                <AltRouteIcon sx={{ mr: 1 }} /> OpenAI
              </ToggleButton>
              <ToggleButton value={MODEL_TYPES.REPLICATE} aria-label="Replicate">
                <AltRouteIcon sx={{ mr: 1 }} /> Replicate
              </ToggleButton>
              <ToggleButton value={MODEL_TYPES.CLAUDE} aria-label="Claude">
                <AltRouteIcon sx={{ mr: 1 }} /> Claude
              </ToggleButton>
              <ToggleButton value={MODEL_TYPES.DEEPAI} aria-label="DeepAI">
                <AltRouteIcon sx={{ mr: 1 }} /> DeepAI
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {isAnalyzing && (
            <Box sx={{ textAlign: 'center', my: 4 }}>
              <CircularProgress sx={{ color: '#64b5f6' }} />
              <Typography variant="body1" sx={{ mt: 2, color: '#e0e0e0' }}>
                Analyzing your hardware image...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isAnalyzing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300 }}>
              <CircularProgress size={60} sx={{ color: '#64b5f6', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#fff' }}>
                Analyzing your hardware image...
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa', mt: 1 }}>
                This may take a moment
              </Typography>
            </Box>
          ) : error ? (
            <ResultCard>
              <ResultHeader>
                <SecurityIcon sx={{ color: '#f44336' }} />
                <Typography variant="h6" component="div" sx={{ ml: 1 }}>
                  Error
                </Typography>
              </ResultHeader>
              <ResultContent>
                <Typography variant="body1" color="error">
                  {error}
                </Typography>
              </ResultContent>
            </ResultCard>
          ) : detailedAnalysis ? (
            <>
              {modelType === MODEL_TYPES.DEEPAI && hasIssues && (
                <IssueWarning severity="error" icon={<HardwareIcon />}>
                  <Typography variant="subtitle1" component="div" fontWeight="bold">
                    Potential Hardware Issues Detected
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {potentialIssues.map((issue, index) => (
                      <Chip 
                        key={index} 
                        label={issue} 
                        color="error" 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </IssueWarning>
              )}
              
              <ResultCard>
                <ResultHeader>
                  <DevicesIcon sx={{ color: '#64b5f6' }} />
                  <Typography variant="h6" component="div" sx={{ ml: 1 }}>
                    Hardware Analysis
                  </Typography>
                </ResultHeader>
                <ResultContent>
                  {modelType === MODEL_TYPES.DEEPAI && imageCaption && (
                    <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(100, 181, 246, 0.2)' }}>
                      <Typography variant="subtitle1" component="div" sx={{ color: '#64b5f6', mb: 1 }}>
                        Image Caption:
                      </Typography>
                      <Typography variant="body1" sx={{ fontStyle: 'italic', color: '#e0e0e0' }}>
                        "{imageCaption}"
                      </Typography>
                    </Box>
                  )}
                  
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#e0e0e0' }}>
                    {detailedAnalysis}
                  </Typography>
                </ResultContent>
              </ResultCard>
            </>
          ) : analysisResults ? (
            <ResultCard>
              <ResultHeader>
                <MemoryIcon sx={{ color: '#64b5f6' }} />
                <Typography variant="h6" component="div" sx={{ ml: 1 }}>
                  Initial Recognition
                </Typography>
              </ResultHeader>
              <ResultContent>
                <Typography variant="subtitle1" sx={{ mb: 2, color: '#64b5f6' }}>
                  Identified concepts:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {analysisResults
                    .filter(concept => concept.value > 0.7)
                    .map((concept, index) => (
                      <TagChip key={index}>
                        {concept.name} ({(concept.value * 100).toFixed(0)}%)
                      </TagChip>
                    ))}
                </Box>
              </ResultContent>
            </ResultCard>
          ) : null}
        </Grid>
      </Grid>

      <Box sx={{ mt: 6, p: 3, backgroundColor: 'rgba(19, 47, 76, 0.5)', borderRadius: '8px' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', display: 'flex', alignItems: 'center' }}>
          <InfoIcon sx={{ mr: 1 }} /> How It Works
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#fff' }}>
                1. Image Recognition
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Clarifai's AI model analyzes your hardware image to identify components and technical elements with confidence scores.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#fff' }}>
                2. Technical Analysis
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                OpenAI's GPT-4 Vision provides detailed technical specifications, compatibility information, and expert insights.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#fff' }}>
                3. Secure Storage
              </Typography>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                Your images and analysis results are securely stored for future reference and to improve our AI's accuracy over time.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Policy Links Section */}
      <Box sx={{ mt: 4, p: 3, backgroundColor: 'rgba(19, 47, 76, 0.5)', borderRadius: '8px' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#64b5f6', display: 'flex', alignItems: 'center' }}>
          <SecurityIcon sx={{ mr: 1 }} /> Policies & Terms
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <PolicyLink href="/terms-of-service" target="_blank">
              <PrivacyTipIcon fontSize="small" /> Terms of Service
            </PolicyLink>
          </Grid>
          <Grid item xs={12} md={4}>
            <PolicyLink href="/privacy-policy" target="_blank">
              <PrivacyTipIcon fontSize="small" /> Privacy Policy
            </PolicyLink>
          </Grid>
          <Grid item xs={12} md={4}>
            <PolicyLink href="/data-usage-policy" target="_blank">
              <PrivacyTipIcon fontSize="small" /> Data Usage Policy
            </PolicyLink>
          </Grid>
        </Grid>
        <Typography variant="body2" sx={{ color: '#aaa', mt: 2 }}>
          By using this service, you agree to our terms and policies regarding the storage and processing of your images.
          We respect your privacy and handle all data in accordance with our data protection guidelines.
        </Typography>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClarifaiAnalyzer;
